import React from "react";
import { BookOpen, List, Pen, PenLine } from "lucide-react";
import { ToolCardShell, type CardStatus } from "../tool";
import { ToolInput, ToolOutput } from "../tool";
import type { ToolCardComponentProps } from "../tool-registry";
import { ToolCardDiff } from "./ToolCardDiff";

const SETTING_TOOL_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; iconColor: string; defaultExpanded: boolean }
> = {
  setting_read: {
    label: "读取设定",
    icon: <BookOpen className="h-4 w-4" />,
    iconColor: "text-blue-500",
    defaultExpanded: false,
  },
  setting_list: {
    label: "设定列表",
    icon: <List className="h-4 w-4" />,
    iconColor: "text-blue-500",
    defaultExpanded: false,
  },
  setting_write: {
    label: "创建设定",
    icon: <Pen className="h-4 w-4" />,
    iconColor: "text-amber-500",
    defaultExpanded: true,
  },
  setting_update: {
    label: "更新设定",
    icon: <PenLine className="h-4 w-4" />,
    iconColor: "text-orange-500",
    defaultExpanded: true,
  },
};

const LEVEL_LABELS: Record<string, string> = {
  global: "全局设定",
  volume: "卷级设定",
  chapter: "章级设定",
};

function formatCharCount(n: number): string {
  return `约 ${n.toLocaleString()} 字`;
}

function getResultCharCount(result: unknown): number {
  if (typeof result === "string") return result.length;
  if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    if (typeof r.content === "string") return r.content.length;
    if (typeof r.text === "string") return r.text.length;
  }
  return 0;
}

function getLevelLabel(level: unknown): string | null {
  if (typeof level === "string" && LEVEL_LABELS[level]) return LEVEL_LABELS[level];
  return null;
}

function getResultCount(result: unknown): number {
  if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    if (Array.isArray(r.settings)) return r.settings.length;
    if (Array.isArray(r.items)) return r.items.length;
    if (typeof r.total === "number") return r.total;
    if (typeof r.count === "number") return r.count;
  }
  return 0;
}

const SettingReadSummary: React.FC<{
  args: Record<string, unknown>;
  result: unknown;
  charCount: number;
}> = ({ args, result, charCount }) => {
  const name = args.name;
  const id = args.id as string | number | undefined;

  if (Array.isArray(name)) {
    return (
      <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <span>读取 {name.length} 项设定</span>
      </div>
    );
  }

  const nameStr = typeof name === "string" ? name : undefined;
  const levelFromResult =
    typeof result === "object" && result !== null
      ? (result as Record<string, unknown>).level
      : undefined;
  const level = getLevelLabel(args.level || levelFromResult);

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {nameStr && <span className="text-foreground font-medium">{nameStr}</span>}
      {!nameStr && id != null && <span>设定 ID: {id}</span>}
      {level && (
        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-blue-700">
          {level}
        </span>
      )}
      {charCount > 0 && <span>{formatCharCount(charCount)}</span>}
    </div>
  );
};

const SettingListSummary: React.FC<{
  args: Record<string, unknown>;
  result: unknown;
}> = ({ args, result }) => {
  const total = getResultCount(result);
  const level = getLevelLabel(args.level);
  const name = typeof args.name === "string" ? args.name : undefined;

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {total > 0 && <span>共 {total} 项</span>}
      {level && (
        <span>筛选: {level}</span>
      )}
      {name && <span>筛选: {name}</span>}
    </div>
  );
};

const SettingWriteSummary: React.FC<{
  args: Record<string, unknown>;
  charCount: number;
}> = ({ args, charCount }) => {
  const name = typeof args.name === "string" ? args.name : undefined;
  const level = getLevelLabel(args.level);
  const settingType = typeof args.setting_type === "string" ? args.setting_type : undefined;
  const scope = typeof args.scope === "string" ? args.scope : undefined;

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {name && <span className="text-foreground font-medium">{name}</span>}
      {level && (
        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-blue-700">
          {level}
        </span>
      )}
      {settingType && (
        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">
          {settingType}
        </span>
      )}
      {charCount > 0 && <span>+{charCount.toLocaleString()} 字</span>}
      {scope && <span>范围: {scope}</span>}
    </div>
  );
};

const SettingUpdateSummary: React.FC<{ args: Record<string, unknown> }> = ({ args }) => {
  const id = args.id as string | number | undefined;
  const oldText = typeof args.old_text === "string" ? args.old_text : "";
  const newText = typeof args.new_text === "string" ? args.new_text : "";
  const useRegex = args.use_regex as boolean | undefined;
  const hasContent = typeof args.content === "string";
  const name = typeof args.name === "string" ? args.name : undefined;

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {id != null && <span>设定 ID: {id}</span>}
      {name && <span className="text-foreground font-medium">{name}</span>}
      {!hasContent && (oldText.length > 0 || newText.length > 0) && (
        <ToolCardDiff additions={newText.length} deletions={oldText.length} />
      )}
      {useRegex && (
        <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-orange-700">
          正则替换
        </span>
      )}
      {hasContent && (
        <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-orange-700">
          完全替换
        </span>
      )}
    </div>
  );
};

export const SettingCard: React.FC<ToolCardComponentProps> = ({ toolCall }) => {
  const cardStatus: CardStatus = toolCall.status || "detected";
  const toolName = toolCall.toolName;
  const args = toolCall.args || {};
  const result = toolCall.result;
  const config = SETTING_TOOL_CONFIG[toolName] || SETTING_TOOL_CONFIG.setting_read;
  const charCount = result ? getResultCharCount(result) : 0;
  const contentCharCount =
    typeof args.content === "string"
      ? args.content.length
      : 0;

  const renderSummary = () => {
    switch (toolName) {
      case "setting_read":
        return <SettingReadSummary args={args} result={result} charCount={charCount} />;
      case "setting_list":
        return <SettingListSummary args={args} result={result} />;
      case "setting_write":
        return <SettingWriteSummary args={args} charCount={contentCharCount} />;
      case "setting_update":
        return <SettingUpdateSummary args={args} />;
      default:
        return null;
    }
  };

  return (
    <ToolCardShell
      toolName={config.label}
      status={cardStatus}
      icon={config.icon}
      iconColor={config.iconColor}
      defaultExpanded={config.defaultExpanded}
    >
      {renderSummary()}
      {toolCall.args && Object.keys(toolCall.args).length > 0 && (
        <ToolInput data={toolCall.args} />
      )}
      {(toolCall.result !== undefined && toolCall.result !== null) || toolCall.error ? (
        <ToolOutput data={toolCall.result} error={toolCall.error} />
      ) : null}
    </ToolCardShell>
  );
};
